/**
 * Report runner (FS-057).
 *
 * The client's must-have was "ready-made reports" and "report builder". This is
 * the first half: pick a saved report, run it, read it, export it. A dealer
 * cannot run a report with curl, so a backend engine on its own delivers
 * nothing.
 *
 * A report is a saved view with `layout: "report"`, which is why there is no
 * separate list to fetch — the same endpoint that serves the view switcher
 * serves this, and a report inherits the locked/collaborative/personal rules
 * without a second permission model.
 */

import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { FileText, Info, Loader2, Lock, Table2, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { apiClient } from "@/api/client";
import Card from "@/components/ui/Card";
import ExportButton from "@/components/ui/ExportButton";

interface SavedViewSummary {
  view_id: string;
  entity_type: string;
  name: string;
  description: string | null;
  visibility: "personal" | "collaborative" | "locked";
  is_system: boolean;
  layout: string;
}

interface ChartSeries {
  key: string;
  label: string;
  values: (string | number | null)[];
}

interface ReportResult {
  name: string;
  description: string | null;
  entity_type: string;
  grouped: boolean;
  headers: string[];
  titles: Record<string, string>;
  rows: Record<string, unknown>[];
  row_count: number;
  group_count?: number;
  total_count?: number;
  totals: Record<string, unknown>;
  included: string;
  notices: string[];
  chart: { labels: string[]; series: ChartSeries[] } | null;
}

const ENTITY_LABELS: Record<string, string> = {
  asset: "Inventory",
  sales_order: "Sales",
  purchase_order: "Purchasing",
  work_order: "Shop",
  customer: "Customers",
  vendor: "Suppliers",
  vendor_bill: "Payables",
};

/** Money-ish values arrive as strings so no cent is lost to a float. */
function looksLikeMoney(key: string): boolean {
  return /cost|total|price|paid|credited|amount|spent|billed|revenue|profit/i.test(
    key,
  );
}

function formatCell(key: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" && looksLikeMoney(key)) {
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber)) {
      return asNumber.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
      });
    }
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return new Date(value).toLocaleDateString();
  }
  return String(value);
}

/**
 * A bar chart from the server's labels and series.
 *
 * Inline SVG rather than a charting dependency: one grouped bar chart does not
 * justify another package in the bundle, and the backend already decided what
 * the series are.
 */
function MiniBarChart({
  labels,
  series,
}: {
  labels: string[];
  series: ChartSeries[];
}) {
  const primary = series[0];
  if (!primary) return null;
  const values = primary.values.map((value) => Number(value ?? 0));
  const max = Math.max(...values, 1);

  return (
    <div className="space-y-1.5">
      {labels.map((label, index) => (
        <div key={`${label}-${index}`} className="flex items-center gap-2">
          <span
            className="w-28 shrink-0 truncate text-right text-[11px] text-[var(--text-secondary)]"
            title={label}
          >
            {label}
          </span>
          <div className="h-4 flex-1 overflow-hidden rounded-sm bg-[var(--surface-sunken)]">
            <div
              className="h-full rounded-sm bg-[var(--color-interactive-primary)] transition-all"
              style={{
                width: `${Math.max(2, ((values[index] ?? 0) / max) * 100)}%`,
              }}
            />
          </div>
          <span className="w-24 shrink-0 text-right text-[11px] font-medium tabular-nums text-[var(--text-primary)]">
            {formatCell(primary.key, primary.values[index] ?? null)}
          </span>
        </div>
      ))}
    </div>
  );
}

function ReportRunnerPage() {
  const [selected, setSelected] = useState<string | null>(null);

  const { data: views, isLoading: loadingViews } = useQuery({
    queryKey: ["saved-views", "reports"],
    queryFn: async () => {
      const response = await apiClient.get("/api/v1/saved-views");
      return (response.data.views as SavedViewSummary[]).filter(
        (view) => view.layout === "report",
      );
    },
  });

  // Open the first report rather than an empty pane — a screen whose first
  // state is "nothing selected" makes the user do work before seeing anything.
  useEffect(() => {
    const first = views?.[0];
    if (!selected && first) setSelected(first.view_id);
  }, [views, selected]);

  const {
    data: result,
    isLoading: running,
    error,
  } = useQuery({
    queryKey: ["report-run", selected],
    queryFn: async () => {
      const response = await apiClient.get(`/api/v1/reports/${selected}/run`);
      return response.data as ReportResult;
    },
    enabled: Boolean(selected),
  });

  const grouped = useMemo(() => {
    const byEntity: Record<string, SavedViewSummary[]> = {};
    for (const view of views ?? []) {
      (byEntity[view.entity_type] ||= []).push(view);
    }
    return byEntity;
  }, [views]);

  return (
    <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
      {/* Report list */}
      <Card className="h-fit overflow-hidden p-0">
        <div className="border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-3 py-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
            Reports
          </h3>
        </div>
        {loadingViews ? (
          <div className="flex items-center gap-2 px-3 py-4 text-xs text-[var(--text-secondary)]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading
          </div>
        ) : !views?.length ? (
          <p className="px-3 py-4 text-xs text-[var(--text-secondary)]">
            No reports yet. The six standard ones arrive with a new
            organization.
          </p>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto">
            {Object.entries(grouped).map(([entity, entityViews]) => (
              <div key={entity}>
                <p className="bg-[var(--surface-sunken)]/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
                  {ENTITY_LABELS[entity] ?? entity}
                </p>
                {entityViews.map((view) => (
                  <button
                    key={view.view_id}
                    type="button"
                    onClick={() => setSelected(view.view_id)}
                    className={clsx(
                      "flex w-full items-start gap-2 border-l-2 px-3 py-2 text-left transition-colors",
                      selected === view.view_id
                        ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/8"
                        : "border-transparent hover:bg-[var(--surface-raised)]",
                    )}
                  >
                    <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--text-tertiary)]" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-medium text-[var(--text-primary)]">
                        {view.name}
                      </span>
                      {view.description && (
                        <span className="mt-0.5 block line-clamp-2 text-[11px] leading-tight text-[var(--text-secondary)]">
                          {view.description}
                        </span>
                      )}
                    </span>
                    {view.visibility === "locked" ? (
                      <span
                        className="mt-0.5 shrink-0"
                        title="Standard report — copy it to change it"
                      >
                        <Lock className="h-3 w-3 text-[var(--text-tertiary)]" />
                      </span>
                    ) : view.visibility === "personal" ? (
                      <span className="mt-0.5 shrink-0" title="Yours only">
                        <User className="h-3 w-3 text-[var(--text-tertiary)]" />
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Result */}
      <div className="min-w-0 space-y-4">
        {!selected ? null : running ? (
          <Card className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
            <Loader2 className="h-4 w-4 animate-spin" /> Running the report
          </Card>
        ) : error ? (
          <Card className="text-sm text-[var(--text-danger)]">
            This report could not be run. It may name a field that no longer
            exists.
          </Card>
        ) : result ? (
          <>
            <Card className="p-0">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-4 py-3">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                    {result.name}
                  </h3>
                  {result.description && (
                    <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
                      {result.description}
                    </p>
                  )}
                </div>
                <ExportButton
                  url={`/api/v1/reports/${selected}/export`}
                  fallbackFilename={result.name}
                  label="Export"
                />
              </div>

              {/* What was counted. FS-027's rule — a number travels with its
                  predicate — applied to a whole page of them. */}
              <div className="flex items-start gap-1.5 px-4 py-2 text-[11px] text-[var(--text-secondary)]">
                <Info className="mt-0.5 h-3 w-3 shrink-0" />
                <span>
                  Counted: {result.included}
                  {result.grouped
                    ? ` · ${result.group_count ?? result.row_count} group${(result.group_count ?? result.row_count) === 1 ? "" : "s"}`
                    : ` · ${result.total_count ?? result.row_count} record${(result.total_count ?? result.row_count) === 1 ? "" : "s"}`}
                </span>
              </div>

              {result.notices.length > 0 && (
                <div className="border-t border-[var(--border-default)] px-4 py-2">
                  {result.notices.map((notice) => (
                    <p
                      key={notice}
                      className="text-[11px] text-[var(--text-warning,var(--text-secondary))]"
                    >
                      {notice}
                    </p>
                  ))}
                </div>
              )}
            </Card>

            {Object.keys(result.totals).length > 0 && (
              <Card className="p-0">
                <div className="border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-4 py-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                    Totals
                  </h4>
                </div>
                <div className="flex flex-wrap gap-x-8 gap-y-3 px-4 py-3">
                  {Object.entries(result.totals).map(([key, value]) => (
                    <div key={key}>
                      <p className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">
                        {result.titles[key] ?? key}
                      </p>
                      <p className="text-lg font-semibold tabular-nums text-[var(--text-primary)]">
                        {formatCell(key, value)}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {result.chart && (
              <Card className="p-0">
                <div className="border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-4 py-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                    {result.chart.series[0]?.label ?? "Chart"}
                  </h4>
                </div>
                <div className="px-4 py-3">
                  <MiniBarChart
                    labels={result.chart.labels}
                    series={result.chart.series}
                  />
                </div>
              </Card>
            )}

            <Card className="overflow-hidden p-0">
              <div className="flex items-center gap-2 border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-4 py-2">
                <Table2 className="h-3.5 w-3.5 text-[var(--text-secondary)]" />
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                  {result.grouped ? "By group" : "Records"}
                </h4>
              </div>
              <div className="max-h-[60vh] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[var(--surface-base)]">
                    <tr className="border-b border-[var(--border-default)] text-left text-xs font-medium text-[var(--text-secondary)]">
                      {result.headers.map((header) => (
                        <th
                          key={header}
                          className="whitespace-nowrap px-4 py-2"
                        >
                          {result.titles[header] ?? header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={result.headers.length}
                          className="px-4 py-6 text-center text-xs text-[var(--text-secondary)]"
                        >
                          Nothing matched. The line above says what was counted.
                        </td>
                      </tr>
                    ) : (
                      result.rows.map((row, index) => (
                        <tr
                          key={index}
                          className="border-b border-[var(--border-subtle,var(--border-default))] last:border-0 hover:bg-[var(--surface-raised)]"
                        >
                          {result.headers.map((header) => (
                            <td
                              key={header}
                              className={clsx(
                                "whitespace-nowrap px-4 py-2 text-[var(--text-primary)]",
                                looksLikeMoney(header) &&
                                  "text-right tabular-nums",
                              )}
                            >
                              {formatCell(header, row[header])}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default ReportRunnerPage;
